import {
  compareElectionValues,
  formatElectionLabel,
  getElectionValue,
} from './painel-meta.js';
import {
  aggregateAll,
  buildPoliticalStrengthData,
  chooseDefaultCargoKey,
  extractCargoKeys,
  normalizeCargoKey,
} from './painel-data.js';
import {
  buildPoliticalDeltaData,
} from './painel-party-lineage.js';

export function buildElectionPairOptions(eleicoes = [], options = {}) {
  const values = [...new Set(eleicoes.map(getElectionValue).filter(Boolean))]
    .sort(compareElectionValues);
  const preferredCargoKey = normalizeCargoKey(options.cargoKey || options.preferredCargoKey || '');
  const pairs = [];
  const seen = new Set();

  const addPair = (from, to) => {
    if (!from || !to || from === to) return;
    const key = `${from}|${to}`;
    if (seen.has(key)) return;
    seen.add(key);
    pairs.push(enrichElectionPair({ from, to }, preferredCargoKey));
  };

  for (let index = 0; index < values.length - 1; index += 1) {
    addPair(values[index], values[index + 1]);
  }

  for (let fromIndex = 0; fromIndex < values.length - 1; fromIndex += 1) {
    for (let toIndex = fromIndex + 1; toIndex < values.length; toIndex += 1) {
      addPair(values[fromIndex], values[toIndex]);
    }
  }

  values.forEach(from => {
    const baseYear = String(from).split('_')[0];
    values
      .filter(to => String(to).split('_')[0] === baseYear && compareElectionValues(from, to) < 0)
      .forEach(to => addPair(from, to));
  });

  if (values.length > 2) addPair(values[0], values[values.length - 1]);
  return sortElectionPairs(pairs, preferredCargoKey);
}

/**
 * @param {{ dadosA?: Record<string, any>, dadosB?: Record<string, any>, anoA?: string, anoB?: string, cargoKey?: string }} options
 */
export function buildElectionComparisonData(options = {}) {
  const { dadosA, dadosB, anoA, anoB, cargoKey = '' } = options;
  const cargosA = extractCargoKeys(dadosA || {});
  const cargosB = extractCargoKeys(dadosB || {});
  const commonCargos = cargosA.filter(cargo => cargosB.includes(cargo));
  const selectedCargoKey = commonCargos.includes(cargoKey)
    ? cargoKey
    : chooseDefaultCargoKey(commonCargos, '');

  if (!selectedCargoKey) {
    return {
      anoA,
      anoB,
      cargoKey: '',
      commonCargos: [],
      aggA: aggregateAll(dadosA || {}, {}),
      aggB: aggregateAll(dadosB || {}, {}),
      partidosDelta: [],
      votosDelta: 0,
    };
  }

  const aggA = aggregateAll(dadosA || {}, { cargoKey: selectedCargoKey });
  const aggB = aggregateAll(dadosB || {}, { cargoKey: selectedCargoKey });
  const strengthA = buildPoliticalStrengthData(aggA);
  const strengthB = buildPoliticalStrengthData(aggB);

  return {
    anoA,
    anoB,
    cargoKey: selectedCargoKey,
    commonCargos,
    aggA,
    aggB,
    partidosDelta: buildPoliticalDeltaData(strengthA.partidos, strengthB.partidos),
    federacoesDelta: buildPoliticalDeltaData(strengthA.federacoes, strengthB.federacoes),
    votosDelta: Number(aggB.totalVotosValidos || 0) - Number(aggA.totalVotosValidos || 0),
  };
}

export function formatElectionPairLabel(pair) {
  const base = formatElectionPairBaseLabel(pair);
  if (pair.enabled === false) return `${base} — sem cargo comparável`;
  if (pair.recommended) return `${base} — recomendado`;
  if (pair.group === 'Segundo turno') return `${base} — turnos`;
  return base;
}

export function buildElectionPairSelectModel(pairs = []) {
  const visiblePairs = pairs.filter(pair => pair?.enabled !== false);
  const hiddenPairsCount = pairs.length - visiblePairs.length;
  const groups = [];
  const groupsByLabel = new Map();

  visiblePairs.forEach(pair => {
    const label = getElectionPairPresentationGroup(pair);
    if (!groupsByLabel.has(label)) {
      const group = { label, pairs: [] };
      groupsByLabel.set(label, group);
      groups.push(group);
    }
    groupsByLabel.get(label).pairs.push(pair);
  });

  const recommended = visiblePairs.find(pair => pair.recommended);
  let primaryNote = '';
  if (recommended) {
    primaryNote = `Comece pelo par recomendado: ${formatElectionPairBaseLabel(recommended)}. Ele compara o mesmo cargo em ciclos equivalentes.`;
  } else if (visiblePairs.length > 0) {
    primaryNote = 'Escolha um par compatível para comparar eleições do mesmo tipo.';
  } else {
    primaryNote = 'Nenhum par temporal compatível para o cargo selecionado.';
  }

  return {
    groups,
    visiblePairs,
    visiblePairsCount: visiblePairs.length,
    hiddenPairsCount,
    primaryNote,
    hiddenDetailsSummary: hiddenPairsCount > 0 ? 'Por que algumas opções não aparecem?' : '',
    hiddenDetails: hiddenPairsCount > 0
      ? `${hiddenPairsCount} pares incompatíveis ficaram ocultos porque misturam eleições sem o mesmo tipo de eleição ou sem cargo equivalente para o recorte atual.`
      : '',
  };
}

function enrichElectionPair(pair, preferredCargoKey = '') {
  const fromCycle = getElectionCycle(pair.from);
  const toCycle = getElectionCycle(pair.to);
  const sameCycle = fromCycle.type && fromCycle.type === toCycle.type;
  const sameYearDifferentTurn = fromCycle.year === toCycle.year && fromCycle.turn !== toCycle.turn;
  const yearDistance = Math.abs(toCycle.year - fromCycle.year);
  const compatibleCargo = preferredCargoKey
    ? isCargoCompatibleWithElectionCycle(preferredCargoKey, fromCycle.type) && isCargoCompatibleWithElectionCycle(preferredCargoKey, toCycle.type)
    : true;
  const consecutiveCycle = sameCycle && yearDistance === 4;
  const enabled = sameYearDifferentTurn || (compatibleCargo && consecutiveCycle) || (!preferredCargoKey && sameCycle);
  const group = getElectionPairGroup({ fromCycle, toCycle, sameYearDifferentTurn, compatibleCargo, enabled });
  const recommended = compatibleCargo && consecutiveCycle;

  return {
    ...pair,
    enabled,
    compatibleCargo,
    recommended,
    group,
    reason: enabled
      ? getElectionPairEnabledReason({ sameYearDifferentTurn, compatibleCargo, recommended })
      : 'Par temporal sem cargo comparável para o recorte selecionado.',
  };
}

function formatElectionPairBaseLabel(pair) {
  return `${formatElectionLabel(pair.from)} x ${formatElectionLabel(pair.to)}`;
}

function getElectionPairPresentationGroup(pair) {
  if (pair?.recommended) {
    if (pair.group === 'Municipais') return 'Municipais recomendadas';
    if (pair.group === 'Gerais') return 'Gerais recomendadas';
    return 'Recomendadas';
  }
  return pair?.group || 'Comparáveis';
}

function sortElectionPairs(pairs, preferredCargoKey = '') {
  const groupWeight = (pair) => {
    if (pair.enabled && pair.compatibleCargo && pair.recommended) return 0;
    if (pair.enabled && pair.group === 'Segundo turno') return preferredCargoKey ? 1 : 0;
    if (pair.enabled) return 2;
    return 3;
  };

  return [...pairs].sort((a, b) => {
    const weight = groupWeight(a) - groupWeight(b);
    if (weight !== 0) return weight;
    const toCompare = compareElectionValues(b.to, a.to);
    if (toCompare !== 0) return toCompare;
    return compareElectionValues(b.from, a.from);
  });
}

function getElectionPairGroup({ fromCycle, toCycle, sameYearDifferentTurn, compatibleCargo, enabled }) {
  if (!enabled) return 'Incompativeis';
  if (sameYearDifferentTurn) return 'Segundo turno';
  if (!compatibleCargo) return 'Outro cargo comparável';
  if (fromCycle.type === 'municipal' && toCycle.type === 'municipal') return 'Municipais';
  if (fromCycle.type === 'geral' && toCycle.type === 'geral') return 'Gerais';
  return 'Comparáveis';
}

function getElectionPairEnabledReason({ sameYearDifferentTurn, compatibleCargo, recommended }) {
  if (sameYearDifferentTurn) return compatibleCargo
    ? 'Compara primeiro e segundo turno do cargo selecionado.'
    : 'Compara turnos de outro cargo disponível na eleição.';
  if (recommended) return 'Par recomendado para comparar o mesmo cargo em ciclos consecutivos.';
  return 'Par comparável.';
}

function getElectionCycle(value) {
  const raw = String(value || '');
  const year = Number(raw.split('_')[0]) || 0;
  const turn = Number(raw.split('_')[1] || 1) || 1;
  let type = '';
  if (year >= 1900) {
    if (year % 4 === 0) type = 'municipal';
    if (year % 4 === 2) type = 'geral';
  }
  return { raw, year, turn, type };
}

function isCargoCompatibleWithElectionCycle(cargoKey, cycleType) {
  const cargo = normalizeCargoKey(cargoKey);
  if (!cargo || !cycleType) return true;
  const municipal = new Set(['prefeitos', 'vereadores']);
  const geral = new Set(['presidentes', 'governadores', 'senadores', 'deputados_federais', 'deputados_estaduais', 'deputados_distritais']);
  if (cycleType === 'municipal') return municipal.has(cargo);
  if (cycleType === 'geral') return geral.has(cargo);
  return true;
}
